// EmployeeForm.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

/**
 * Section component (kept outside main component)
 */
const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="mb-3 border">
      <div
        className=" fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen(!open)}
      >
        <span>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
}

export default function EmployeeForm() {
  const navigate = useNavigate();

  const countries = {
    India: ["Maharashtra", "Karnataka", "Gujarat", "Delhi", "Tamil Nadu"],
    "United States": ["California", "Texas", "New York", "Florida", "Illinois"],
    "United Kingdom": ["England", "Scotland", "Wales", "Northern Ireland"],
  };

  const countryCodes = {
    India: "+91",
    "United States": "+1",
    "United Kingdom": "+44",
  };

  const initialForm = {
    employeeId: "",
    firstName: "",
    lastName: "",
    email: "",
    pemail: "",
    phone: "",
    dob: "",
    gender: "",
    designation: "",
    department: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    doj: "",
    onboardingDate: "",
    confirmationDate: "",
    mstatus: "",
    doa: "",
    dol: "",
    leavingReason: "",
    contact: "",
    alno: "",
    superviser: "",
    degree: "",
    institution: "",
    fieldOfStudy: "",
    eduStartDate: "",
    eduEndDate: "",
    grade: "",
    sameAsPermanent: false,
    currStreet1: "",
    currStreet2: "",
    currCity: "",
    currState: "",
    currPostalCode: "",
    currCountry: "",
    password: "",
    confirmPassword: "",
    roles: [],
    status: "",
    selectedRole: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    branch: "",
    giveAccess: false,
    manualPassword: false,
    sendNotification: false,
    paymentType: "",
  };

  const [formData, setFormData] = useState(initialForm);
  const [photo, setPhoto] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [activeTab, setActiveTab] = useState("comm");
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [roles, setRoles] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [employees, setEmployees] = useState([]);
  const [isActive] = useState(true); // kept for UI
  const [navbarColor, setNavbarColor] = useState("#080235");

  // Validation errors
  const [errors, setErrors] = useState({});
  // Track if field touched (for showing validation after user touched)
  const [touched, setTouched] = useState({});

  // refs map to focus invalid field
  const refs = useRef({});

  useEffect(() => {
    const token = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");

    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
      return;
    }

    // ✅ check admin role
    if (!roles.includes("ROLE_ADMIN")) {
      toast.error("You are not authorized to access this page.");
      navigate("/");
      return;
    }
  }, [navigate]);


  useEffect(() => {

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");

    if (!token || !userId) return;

    fetch(`${API_BASE_URL}/api/appearance/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data && data.navbarColor) {
          setNavbarColor(data.navbarColor);
        }
      })
      .catch(err => console.log("Appearance fetch error:", err));

  }, []);

  // load roles, employees, designations, departments
  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(`${API_BASE_URL}/api/roles/all`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.json())
      .then((data) => setRoles(Array.isArray(data) ? data.map((r) => r.name || r) : []))
      .catch(() => setErrorMessage("Unable to load roles"));

    // fetch(`${API_BASE_URL}/api/employees/active`, {
    //   headers: token ? { Authorization: `Bearer ${token}` } : {},
    // })
    //   .then((res) => res.json())
    //   .then((data) => setEmployees(data))
    //   .catch(() => setErrorMessage("Unable to load employees"));

    fetch(`${API_BASE_URL}/api/employees/active`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.json())
      .then((data) => {
        const filtered = data.filter(emp =>
          emp.user.roles?.includes("ROLE_MANAGER") ||
          emp.user.roles?.includes("ROLE_ADMIN")
        );

        setEmployees(filtered);
      })
      .catch(() => setErrorMessage("Unable to load employees"));


    fetch(`${API_BASE_URL}/api/designations/all`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.json())
      .then((data) => setDesignations(Array.isArray(data) ? data.map((d) => d.name || d) : []))
      .catch(() => { });

    fetch(`${API_BASE_URL}/api/departments/all`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.json())
      .then((data) => setDepartments(Array.isArray(data) ? data.map((d) => d.name || d) : []))
      .catch(() => { });
  }, []);

  useEffect(() => {
    // inject bootstrap + icons if your project doesn't already have them
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  // helper to set refs
  const setRef = (name) => (el) => {
    refs.current[name] = el;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => {
      let updated = { ...prev, [name]: type === "checkbox" ? checked : value };

      // Same as permanent logic
      if (name === "sameAsPermanent" && checked) {
        updated = {
          ...updated,
          currStreet1: updated.street1,
          currStreet2: updated.street2,
          currCity: updated.city,
          currState: updated.state,
          currPostalCode: updated.postalCode,
        };
      }

      // If permanent address changed while sameAsPermanent true -> uncheck and clear current
      if (
        (name === "street1" ||
          name === "street2" ||
          name === "city" ||
          name === "state" ||
          name === "postalCode") &&
        updated.sameAsPermanent
      ) {
        updated.sameAsPermanent = false;
        updated.currStreet1 = "";
        updated.currStreet2 = "";
        updated.currCity = "";
        updated.currState = "";
        updated.currPostalCode = "";
      }

      return updated;
    });

    // mark touched for this field
    setTouched((t) => ({ ...t, [name]: true }));
  };

  const handleFileChange = (e) => {
    setPhoto(e.target.files[0]);
    setTouched((t) => ({ ...t, photo: true }));
  };

  // Validation helpers
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9]{10}$/;
  const postalRegex = /^[0-9]{5,6}$/;
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

  const validateForm = () => {
    const newErrors = {};

    // Primary
    if (!formData.firstName?.trim()) newErrors.firstName = "First name is required.";
    if (!/^[A-Za-z\s'-]+$/.test(formData.firstName || "")) {
      if (!newErrors.firstName) newErrors.firstName = "Only alphabets allowed.";
    }

    if (!formData.lastName?.trim()) newErrors.lastName = "Last name is required.";
    if (!/^[A-Za-z\s'-]+$/.test(formData.lastName || "")) {
      if (!newErrors.lastName) newErrors.lastName = "Only alphabets allowed.";
    }

    if (!formData.designation) newErrors.designation = "Job role is required.";
    if (!formData.department) newErrors.department = "Department is required.";

    // Email / Phone
    if (!formData.email?.trim()) newErrors.email = "Work email is required.";
    else if (!emailRegex.test(formData.email)) newErrors.email = "Enter a valid email.";

    if (!formData.country) newErrors.country = "Country is required.";

    if (!formData.phone?.trim()) newErrors.phone = "Office phone is required.";
    else if (!phoneRegex.test(formData.phone)) newErrors.phone = "Phone must be 10 digits.";

    // HR tab
    if (!formData.pemail?.trim()) newErrors.pemail = "Personal email is required.";
    else if (!emailRegex.test(formData.pemail)) newErrors.pemail = "Enter a valid personal email.";

    if (!formData.contact?.trim()) newErrors.contact = "Contact number is required.";
    else if (!phoneRegex.test(formData.contact)) newErrors.contact = "Contact must be 10 digits.";

    // if (!formData.alno?.trim()) newErrors.alno = "Alternative number is required.";
    // else if (!phoneRegex.test(formData.alno)) newErrors.alno = "Alternative number must be 10 digits.";

    if (formData.alno && !phoneRegex.test(formData.alno)) {
      newErrors.alno = "Alternative number must be 10 digits.";
    }


    if (!formData.dob) newErrors.dob = "Date of birth is required.";

    if (!formData.mstatus) {
      newErrors.mstatus = "Marital status is required.";
    }

    if (formData.mstatus === "Married" && !formData.doa) {
      newErrors.doa = "Anniversary date is required for married status.";
    }

    if (!formData.gender) newErrors.gender = "Gender is required.";
    if (!formData.doj) newErrors.doj = "Date of joining is required.";
    if (!photo) newErrors.photo = "Photo is required.";

    // Education
    if (!formData.degree?.trim()) newErrors.degree = "Degree is required.";
    if (!formData.institution?.trim()) newErrors.institution = "University is required.";

    // Permanent Address
    if (!formData.street1?.trim()) newErrors.street1 = "Street 1 is required.";
    if (!formData.city?.trim()) newErrors.city = "City is required.";
    if (!formData.state?.trim()) newErrors.state = "State is required.";
    if (!formData.postalCode?.trim()) newErrors.postalCode = "Postal code is required.";
    else if (!postalRegex.test(formData.postalCode)) newErrors.postalCode = "Enter a valid postal code (5-6 digits).";

    // Current Address (if not same as permanent)
    if (!formData.sameAsPermanent) {
      if (!formData.currStreet1?.trim()) newErrors.currStreet1 = "Current street is required.";
      if (!formData.currCity?.trim()) newErrors.currCity = "Current city is required.";
      if (!formData.currState?.trim()) newErrors.currState = "Current state is required.";
      if (!formData.currPostalCode?.trim()) newErrors.currPostalCode = "Current postal code is required.";
      else if (!postalRegex.test(formData.currPostalCode)) newErrors.currPostalCode = "Enter a valid postal code (5-6 digits).";
    }

    // Employee ID
    if (!formData.employeeId?.trim()) {
      newErrors.employeeId = "Employee ID is required.";
    } else if (!/^[A-Za-z0-9-_]+$/.test(formData.employeeId)) {
      newErrors.employeeId = "Employee ID can contain letters, numbers, - or _.";
    }


    // Access tab
    if (formData.giveAccess) {
      if (!formData.roles || formData.roles.length === 0) newErrors.roles = "At least one role is required when giving access.";
      if (!formData.status) newErrors.status = "Status is required when giving access.";

      if (formData.manualPassword) {
        if (!formData.password) newErrors.password = "Password is required.";
        else if (!passwordRegex.test(formData.password)) newErrors.password = "Password must be 8+ chars and include uppercase, lowercase, number, and special character.";

        if (!formData.confirmPassword) newErrors.confirmPassword = "Please confirm password.";
        else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Passwords do not match.";
      }
    }

    // Bank details: if any bank field is filled then all are required
    const bankFieldsPresent =
      formData.bankName ||
      formData.accountNumber ||
      formData.ifscCode ||
      formData.branch;

    if (bankFieldsPresent) {
      // Required validations
      if (!formData.bankName)
        newErrors.bankName = "Bank name is required when adding bank details.";

      if (!formData.accountNumber)
        newErrors.accountNumber = "Account number is required when adding bank details.";

      if (!formData.ifscCode)
        newErrors.ifscCode = "IFSC code is required when adding bank details.";

      if (!formData.branch)
        newErrors.branch = "Branch is required when adding bank details.";

      // Pattern validations

      // 1️⃣ Bank Name: alphabets + spaces only
      if (formData.bankName && !/^[A-Za-z ]+$/.test(formData.bankName)) {
        newErrors.bankName = "Bank name must contain only alphabets.";
      }

      // 2️⃣ Account Number: digits only
      if (formData.accountNumber && !/^[0-9]+$/.test(formData.accountNumber)) {
        newErrors.accountNumber = "Account number must contain only digits.";
      }

      // 3️⃣ IFSC: alphanumeric only
      if (formData.ifscCode && !/^[A-Za-z0-9]+$/.test(formData.ifscCode)) {
        newErrors.ifscCode = "IFSC code must be alphanumeric.";
      }

      // 4️⃣ Branch Name: letters, numbers & spaces allowed
      if (formData.branch && !/^[A-Za-z0-9 ]+$/.test(formData.branch)) {
        newErrors.branch = "Branch name contains invalid characters.";
      }
    }

    return newErrors;

  };

  // set validity class helper
  const inputClass = (field) => {
    if (errors[field]) return "form-control is-invalid";
    if (touched[field] && !errors[field]) return "form-control is-valid";
    return "form-control";
  };

  const selectClass = (field) => {
    if (errors[field]) return "form-select is-invalid";
    if (touched[field] && !errors[field]) return "form-select is-valid";
    return "form-select";
  };

  const handleAddRole = () => {
    if (formData.selectedRole && !(formData.roles || []).includes(formData.selectedRole)) {
      setFormData((prev) => ({
        ...prev,
        roles: [...(prev.roles || []), prev.selectedRole],
        selectedRole: "",
      }));
      setTouched((t) => ({ ...t, roles: true }));
    }
  };

  const focusFirstError = (errs) => {
    if (!errs || Object.keys(errs).length === 0) return;
    const firstKey = Object.keys(errs)[0];
    const el = refs.current[firstKey];
    if (el && typeof el.focus === "function") {
      el.focus();
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      // fallback to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    setErrors(newErrors);

    // mark all as touched to show all feedback
    const touchedAll = {};
    Object.keys(initialForm).forEach((k) => (touchedAll[k] = true));
    setTouched(touchedAll);

    if (Object.keys(newErrors).length > 0) {
      focusFirstError(newErrors);
      toast.error("Please fix form errors before submitting.");
      return;
    }

    // Build payload - same as your original logic, but with explicit exclusions
    const data = new FormData();
    Object.entries(formData).forEach(([k, v]) => {
      if (
        k !== "sameAsPermanent" &&
        k !== "confirmPassword" &&
        k !== "selectedRole" &&
        !["manualPassword"].includes(k)
      ) {
        // roles may be array -> append as JSON string or multiple entries depending on backend
        if (k === "roles" && Array.isArray(v)) {
          data.append(k, JSON.stringify(v));
        } else {
          data.append(k, v === undefined || v === null ? "" : v);
        }
      }
    });

    if (photo) data.append("photo", photo);

    try {
      const token = localStorage.getItem("token");

      // 1) Save Employee
      const res = await fetch(`${API_BASE_URL}/api/employees`, {
        method: "POST",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
        body: data,
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        toast.error("Failed to add employee. " + (errText || ""));
        return;
      }

      const savedEmployee = await res.json();

      // 2) Access creation if required
      if (formData.giveAccess) {
        let finalPassword = formData.password;
        if (!formData.manualPassword) {
          finalPassword = Math.random().toString(36).slice(-8);
          setGeneratedPassword(finalPassword);
        }

        const accessPayload = {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          password: finalPassword,
          roles: formData.roles,
          status: formData.status,
          sendNotification: formData.sendNotification || false,
        };

        const accessRes = await fetch(`${API_BASE_URL}/api/auth/register`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify(accessPayload),
        });

        if (!accessRes.ok) {
          toast.warn("Employee saved, but access creation failed.");
        }
      }

      // 3) Bank details if present
      if (formData.bankName || formData.accountNumber) {
        const bankPayload = {
          bankName: formData.bankName,
          accountNumber: formData.accountNumber,
          ifscCode: formData.ifscCode,
          branch: formData.branch,
          paymentType: formData.paymentType,
        };

        const bankRes = await fetch(
          `${API_BASE_URL}/api/bank-details/${savedEmployee.id}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token ? `Bearer ${token}` : "",
            },
            body: JSON.stringify(bankPayload),
          }
        );

        if (!bankRes.ok) {
          toast.warn("Employee saved, but bank details failed.");
        }
      }

      toast.success("Employee added successfully!", {
        onClose: () => navigate("/allemployee")
      });
      // reset
      setFormData(initialForm);
      setPhoto(null);
      setErrors({});
      setTouched({});
      setGeneratedPassword("");
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while adding the employee.");
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top">
        <AdminNavbar />
      </div>

      <div className="container-fluid my-4" style={{ paddingTop: "110px" }}>
        <div className="mb-3">
          <h4 className="m-0">👤 Employee</h4>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
              <button type="submit" form="employeeForm" className="btn btn-primary me-2">
                Save
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                Cancel
              </button>
            </div>

            <a href="/allemployee" className="text-primary text-decoration-none me-3">
              List
            </a>
          </div>
        </div>

        <form id="employeeForm" onSubmit={handleSubmit} encType="multipart/form-data">
          {/* Primary Information */}
          <Section title="Primary Information">
            <div className="col-md-3">
              <label className="form-label">
                First Name <span className="text-danger">*</span>
              </label>
              <input
                ref={setRef("firstName")}
                type="text"
                name="firstName"
                className={inputClass("firstName")}
                value={formData.firstName}
                onChange={handleChange}
              />
              {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Last Name <span className="text-danger">*</span>
              </label>
              <input
                ref={setRef("lastName")}
                type="text"
                name="lastName"
                className={inputClass("lastName")}
                value={formData.lastName}
                onChange={handleChange}
              />
              {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Job Role <span className="text-danger">*</span>
              </label>
              <select
                ref={setRef("designation")}
                name="designation"
                className={selectClass("designation")}
                value={formData.designation}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {designations.map((d, i) => (
                  <option key={i} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              {errors.designation && <div className="invalid-feedback">{errors.designation}</div>}
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Department <span className="text-danger">*</span>
              </label>
              <select
                ref={setRef("department")}
                name="department"
                className={selectClass("department")}
                value={formData.department}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {departments.map((d, i) => (
                  <option key={i} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              {errors.department && <div className="invalid-feedback">{errors.department}</div>}
            </div>

            <div className="col-md-3">
              <label className="form-label">Superviser/Manager</label>
              <select
                ref={setRef("superviser")}
                name="superviser"
                className="form-select"
                value={formData.superviser}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName}
                  </option>
                ))}
              </select>
            </div>
            {/* Employee ID */}
            <div className="col-md-3">
              <label className="form-label">
                Employee ID <span className="text-danger">*</span>
              </label>
              <input
                ref={setRef("employeeId")}
                type="text"
                name="employeeId"
                className={inputClass("employeeId")}
                value={formData.employeeId}
                onChange={handleChange}
                placeholder="EMP-001"
              />
              {errors.employeeId && (
                <div className="invalid-feedback">{errors.employeeId}</div>
              )}
            </div>
          </Section>

          {/* Email / Phone */}
          <Section title="Email | Phone |">
            <div className="col-md-3">
              <label className="form-label">
                Work Email <span className="text-danger">*</span>
              </label>
              <input
                ref={setRef("email")}
                type="email"
                name="email"
                className={inputClass("email")}
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Country <span className="text-danger">*</span>
              </label>
              <select
                ref={setRef("country")}
                name="country"
                className={selectClass("country")}
                value={formData.country}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {Object.keys(countries).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              {errors.country && <div className="invalid-feedback">{errors.country}</div>}
            </div>

            {/* <div className="col-md-3">
              <label className="form-label">Code</label>
              <input type="text" className="form-control" readOnly value={countryCodes[formData.country] || ""} />
            </div> */}

            <div className="col-auto" style={{ width: "80px" }}>
              <label className="form-label">Code</label>
              <input
                type="text"
                className="form-control"
                readOnly
                value={countryCodes[formData.country] || ""}
              />
            </div>



            <div className="col-md-3 ">
              <label className="form-label">
                Office Phone <span className="text-danger">*</span>
              </label>
              <input
                ref={setRef("phone")}
                type="tel"
                name="phone"
                className={inputClass("phone")}
                value={formData.phone}
                onChange={handleChange}
              />
              {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
            </div>
          </Section>

          {/* Tabs */}
          <ul className="nav nav-tabs mb-2" style={{ backgroundColor: navbarColor }}>
            {[
              ["comm", "Communication"],
              ["hr", "Human Resources"],
              // ["access", "Access"],
              ["leaveinfo", "Leave Info"],
              ["bank", "Bank Payment Details"],
              ["system", "System Information"],
            ].map(([id, label]) => (
              <li className="nav-item" key={id}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === id ? "active" : ""}`}
                  onClick={() => setActiveTab(id)}
                  style={{
                    color: activeTab === id ? navbarColor : "#fff",
                    backgroundColor: activeTab === id ? "#fff" : "transparent",
                    fontWeight: activeTab === id ? "600" : "normal",
                  }}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>

          <div className="border p-3">
            {activeTab === "comm" && <div>📩 Communication Tab Content</div>}

            {activeTab === "hr" && (
              <>
                <Section title="Primary Information">
                  <div className="col-md-3">
                    <label className="form-label">
                      Personal Email <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("pemail")}
                      type="email"
                      name="pemail"
                      className={inputClass("pemail")}
                      value={formData.pemail}
                      onChange={handleChange}
                    />
                    {errors.pemail && <div className="invalid-feedback">{errors.pemail}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      Contact No <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("contact")}
                      type="tel"
                      name="contact"
                      className={inputClass("contact")}
                      value={formData.contact}
                      onChange={handleChange}
                    />
                    {errors.contact && <div className="invalid-feedback">{errors.contact}</div>}
                  </div>

                  <div className="col-md-3">
                    {/* <label className="form-label">
                      Alternative No <span className="text-danger">*</span>
                    </label> */}
                    <label className="form-label">Alternative No</label>
                    <input
                      ref={setRef("alno")}
                      type="tel"
                      name="alno"
                      className={inputClass("alno")}
                      value={formData.alno}
                      onChange={handleChange}
                    />
                    {errors.alno && <div className="invalid-feedback">{errors.alno}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      Date of Birth <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("dob")}
                      type="date"
                      name="dob"
                      className={inputClass("dob")}
                      value={formData.dob}
                      onChange={handleChange}
                    />
                    {errors.dob && <div className="invalid-feedback">{errors.dob}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      Marital Status <span className="text-danger">*</span>
                    </label>
                    <select
                      ref={setRef("mstatus")}
                      name="mstatus"
                      className={selectClass("mstatus")}
                      value={formData.mstatus}
                      onChange={(e) => {
                        handleChange(e);
                        // Optional: reset doa if Unmarried
                        if (e.target.value === "Unmarried") {
                          setFormData(prev => ({ ...prev, doa: "" }));
                        }
                      }}
                    >
                      <option value="">Select</option>
                      <option>Married</option>
                      <option>Unmarried</option>
                    </select>
                    {errors.mstatus && <div className="invalid-feedback">{errors.mstatus}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Anniversary Date</label>
                    <input
                      ref={setRef("doa")}
                      type="date"
                      name="doa"
                      className={inputClass("doa")}
                      value={formData.doa}
                      onChange={handleChange}
                      disabled={formData.mstatus !== "Married"} // ✅ disabled if not Married
                    />
                    {errors.doa && <div className="invalid-feedback">{errors.doa}</div>}
                  </div>


                  <div className="col-md-3">
                    <label className="form-label">
                      Gender <span className="text-danger">*</span>
                    </label>
                    <select
                      ref={setRef("gender")}
                      name="gender"
                      className={selectClass("gender")}
                      value={formData.gender}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                    {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      Date of Joining <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("doj")}
                      type="date"
                      name="doj"
                      className={inputClass("doj")}
                      value={formData.doj}
                      onChange={handleChange}
                    />
                    {errors.doj && <div className="invalid-feedback">{errors.doj}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      Onboarding Date
                    </label>
                    <input
                      ref={setRef("onboardingDate")}
                      type="date"
                      name="onboardingDate"
                      className={inputClass("onboardingDate")}
                      value={formData.onboardingDate}
                      onChange={handleChange}
                    />
                    {errors.onboardingDate && (
                      <div className="invalid-feedback">{errors.onboardingDate}</div>
                    )}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      Confirmation Date
                    </label>
                    <input
                      ref={setRef("confirmationDate")}
                      type="date"
                      name="confirmationDate"
                      className={inputClass("confirmationDate")}
                      value={formData.confirmationDate}
                      onChange={handleChange}
                    />
                    {errors.confirmationDate && (
                      <div className="invalid-feedback">{errors.confirmationDate}</div>
                    )}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Date of Leaving</label>
                    <input
                      ref={setRef("dol")}
                      type="date"
                      name="dol"
                      className={inputClass("dol")}
                      value={formData.dol}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Leaving Reason</label>
                    <input
                      ref={setRef("leavingReason")}
                      type="text"
                      name="leavingReason"
                      className="form-control"
                      value={formData.leavingReason}
                      onChange={handleChange}
                      placeholder="Enter leaving reason"
                      disabled={!formData.dol}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      Profile Photo <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("photo")}
                      type="file"
                      accept="image/*"
                      className={inputClass("photo")}
                      onChange={handleFileChange}
                    />
                  </div>
                </Section>

                <Section title="Education Information">
                  <div className="col-md-3">
                    <label className="form-label">
                      Degree <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("degree")}
                      type="text"
                      name="degree"
                      className={inputClass("degree")}
                      value={formData.degree}
                      onChange={handleChange}
                    />
                    {errors.degree && <div className="invalid-feedback">{errors.degree}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      University <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("institution")}
                      type="text"
                      name="institution"
                      className={inputClass("institution")}
                      value={formData.institution}
                      onChange={handleChange}
                    />
                    {errors.institution && <div className="invalid-feedback">{errors.institution}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Stream</label>
                    <input
                      ref={setRef("fieldOfStudy")}
                      type="text"
                      name="fieldOfStudy"
                      className={inputClass("fieldOfStudy")}
                      value={formData.fieldOfStudy}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Start Date</label>
                    <input
                      ref={setRef("eduStartDate")}
                      type="date"
                      name="eduStartDate"
                      className={inputClass("eduStartDate")}
                      value={formData.eduStartDate}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">End Date</label>
                    <input
                      ref={setRef("eduEndDate")}
                      type="date"
                      name="eduEndDate"
                      className={inputClass("eduEndDate")}
                      value={formData.eduEndDate}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Grade / Percentage</label>
                    <input
                      ref={setRef("grade")}
                      type="text"
                      name="grade"
                      className={inputClass("grade")}
                      value={formData.grade}
                      onChange={handleChange}
                    />
                  </div>
                </Section>

                <Section title="Address Information">
                  <div className="col-12 mb-2">
                    <div className="form-check">
                      <input
                        ref={setRef("sameAsPermanent")}
                        type="checkbox"
                        id="sameAsPermanent"
                        name="sameAsPermanent"
                        className="form-check-input"
                        checked={formData.sameAsPermanent}
                        onChange={handleChange}
                      />
                      <label htmlFor="sameAsPermanent" className="form-check-label">
                        Current Address same as Permanent
                      </label>
                    </div>
                  </div>

                  <h6 className="fw-bold">Permanent Address</h6>
                  <div className="col-md-3">
                    <label className="form-label">
                      Street 1 <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("street1")}
                      type="text"
                      name="street1"
                      className={inputClass("street1")}
                      value={formData.street1}
                      onChange={handleChange}
                    />
                    {errors.street1 && <div className="invalid-feedback">{errors.street1}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Street 2</label>
                    <input
                      ref={setRef("street2")}
                      type="text"
                      name="street2"
                      className={inputClass("street2")}
                      value={formData.street2}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      City <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("city")}
                      type="text"
                      name="city"
                      className={inputClass("city")}
                      value={formData.city}
                      onChange={handleChange}
                    />
                    {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      State <span className="text-danger">*</span>
                    </label>
                    <select
                      ref={setRef("state")}
                      name="state"
                      className={selectClass("state")}
                      value={formData.state}
                      onChange={handleChange}
                      disabled={!formData.country}
                    >
                      <option value="">Select</option>
                      {formData.country &&
                        countries[formData.country].map((st) => (
                          <option key={st} value={st}>
                            {st}
                          </option>
                        ))}
                    </select>
                    {errors.state && <div className="invalid-feedback">{errors.state}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      Postal Code <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("postalCode")}
                      type="text"
                      name="postalCode"
                      className={inputClass("postalCode")}
                      value={formData.postalCode}
                      onChange={handleChange}
                    />
                    {errors.postalCode && <div className="invalid-feedback">{errors.postalCode}</div>}
                  </div>

                  <h6 className="fw-bold mt-3">Current Address</h6>
                  <div className="col-md-3">
                    <label className="form-label">
                      Street 1 <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("currStreet1")}
                      type="text"
                      name="currStreet1"
                      className={inputClass("currStreet1")}
                      value={formData.currStreet1}
                      onChange={handleChange}
                      disabled={formData.sameAsPermanent}
                    />
                    {errors.currStreet1 && <div className="invalid-feedback">{errors.currStreet1}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Street 2</label>
                    <input
                      ref={setRef("currStreet2")}
                      type="text"
                      name="currStreet2"
                      className={inputClass("currStreet2")}
                      value={formData.currStreet2}
                      onChange={handleChange}
                      disabled={formData.sameAsPermanent}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      City <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("currCity")}
                      type="text"
                      name="currCity"
                      className={inputClass("currCity")}
                      value={formData.currCity}
                      onChange={handleChange}
                      disabled={formData.sameAsPermanent}
                    />
                    {errors.currCity && <div className="invalid-feedback">{errors.currCity}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      State <span className="text-danger">*</span>
                    </label>
                    <select
                      ref={setRef("currState")}
                      name="currState"
                      className={selectClass("currState")}
                      value={formData.currState}
                      onChange={handleChange}
                      disabled={formData.sameAsPermanent || !formData.country}
                    >
                      <option value="">Select</option>
                      {formData.country &&
                        countries[formData.country].map((st) => (
                          <option key={st} value={st}>
                            {st}
                          </option>
                        ))}
                    </select>
                    {errors.currState && <div className="invalid-feedback">{errors.currState}</div>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      Postal Code <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={setRef("currPostalCode")}
                      type="text"
                      name="currPostalCode"
                      className={inputClass("currPostalCode")}
                      value={formData.currPostalCode}
                      onChange={handleChange}
                      disabled={formData.sameAsPermanent}
                    />
                    {errors.currPostalCode && <div className="invalid-feedback">{errors.currPostalCode}</div>}
                  </div>
                </Section>
              </>
            )}

            {activeTab === "time" && <div>⏱️ Time Tracking Tab Content</div>}
            {activeTab === "commission" && <div>💰 Commission Tab Content</div>}
            {activeTab === "marketing" && <div>📊 Marketing Tab Content</div>}

            {activeTab === "bank" && (
              <div className="p-3">
                <h5 className="mb-3"> Bank Payment Details</h5>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Bank Name</label>
                    <input
                      ref={setRef("bankName")}
                      type="text"
                      className={inputClass("bankName")}
                      name="bankName"
                      value={formData.bankName || ""}
                      onChange={handleChange}
                      placeholder="Enter bank name"
                    />
                    {errors.bankName && <div className="invalid-feedback">{errors.bankName}</div>}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">Account Number</label>
                    <input
                      ref={setRef("accountNumber")}
                      type="text"
                      className={inputClass("accountNumber")}
                      name="accountNumber"
                      value={formData.accountNumber || ""}
                      onChange={handleChange}
                      placeholder="Enter account number"
                    />
                    {errors.accountNumber && <div className="invalid-feedback">{errors.accountNumber}</div>}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">IFSC Code</label>
                    <input
                      ref={setRef("ifscCode")}
                      type="text"
                      className={inputClass("ifscCode")}
                      name="ifscCode"
                      value={formData.ifscCode || ""}
                      onChange={handleChange}
                      placeholder="Enter IFSC code"
                    />
                    {errors.ifscCode && <div className="invalid-feedback">{errors.ifscCode}</div>}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Branch</label>
                    <input
                      ref={setRef("branch")}
                      type="text"
                      className={inputClass("branch")}
                      name="branch"
                      value={formData.branch || ""}
                      onChange={handleChange}
                      placeholder="Enter branch name"
                    />
                    {errors.branch && <div className="invalid-feedback">{errors.branch}</div>}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "access" && (
              <div>
                <h5 className="mb-3"> Access Information</h5>

                <div className="form-check mb-3">
                  <input
                    ref={setRef("giveAccess")}
                    type="checkbox"
                    className="form-check-input"
                    id="giveAccess"
                    name="giveAccess"
                    checked={formData.giveAccess || false}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, giveAccess: e.target.checked }));
                      setTouched((t) => ({ ...t, giveAccess: true }));
                    }}
                  />
                  <label className="form-check-label" htmlFor="giveAccess">
                    Give Access
                  </label>
                </div>

                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Name</label>
                    <input type="text" className="form-control" value={`${formData.firstName} ${formData.lastName}`} readOnly />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-control" value={formData.email} readOnly />
                  </div>

                  <div className="col-md-3">
                    <div className="form-check mt-4">
                      <input
                        ref={setRef("manualPassword")}
                        type="checkbox"
                        className="form-check-input"
                        id="manualPassword"
                        name="manualPassword"
                        disabled={!formData.giveAccess}
                        checked={formData.manualPassword || false}
                        onChange={(e) => setFormData((prev) => ({ ...prev, manualPassword: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="manualPassword">
                        Manually Assign or Change Password
                      </label>
                    </div>
                  </div>

                  {!formData.manualPassword && generatedPassword && (
                    <div className="col-md-3">
                      <label className="form-label">Generated Password</label>
                      <input type="text" className="form-control" value={generatedPassword} readOnly />
                    </div>
                  )}

                  {formData.manualPassword && (
                    <>
                      <div className="col-md-3">
                        <label className="form-label">Password</label>
                        <input
                          ref={setRef("password")}
                          type="password"
                          name="password"
                          className={inputClass("password")}
                          disabled={!formData.giveAccess}
                          value={formData.password}
                          onChange={handleChange}
                        />
                        {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">Confirm Password</label>
                        <input
                          ref={setRef("confirmPassword")}
                          type="password"
                          name="confirmPassword"
                          className={inputClass("confirmPassword")}
                          disabled={!formData.giveAccess}
                          value={formData.confirmPassword || ""}
                          onChange={handleChange}
                        />
                        {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
                      </div>
                    </>
                  )}

                  <div className="col-md-3">
                    <label className="form-label">Roles</label>
                    <div className="d-flex gap-2">
                      <select
                        className="form-select"
                        disabled={!formData.giveAccess}
                        value={formData.selectedRole || ""}
                        onChange={(e) => setFormData((prev) => ({ ...prev, selectedRole: e.target.value }))}
                      >
                        <option value="">Select Role</option>
                        {roles.map((role, idx) => (
                          <option key={idx} value={role} disabled={(formData.roles || []).includes(role)}>
                            {role}
                          </option>
                        ))}
                      </select>

                      <button type="button" className="btn btn-primary" disabled={!formData.giveAccess} onClick={handleAddRole}>
                        Add
                      </button>
                    </div>

                    {(formData.roles || []).length > 0 && (
                      <div className="mt-2">
                        {formData.roles.map((role, idx) => (
                          <span key={idx} className="badge bg-secondary me-2">
                            {role}
                            <button
                              type="button"
                              className="btn-close btn-close-white btn-sm ms-1"
                              aria-label={`Remove ${role}`}
                              onClick={() => setFormData((prev) => ({ ...prev, roles: prev.roles.filter((r) => r !== role) }))}
                            />
                          </span>
                        ))}
                      </div>
                    )}

                    {errors.roles && <small className="text-danger d-block mt-1">{errors.roles}</small>}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Status</label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="statusCheckbox"
                        checked={formData.status === "Active"}
                        disabled={!formData.giveAccess}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            status: e.target.checked ? "Active" : "Inactive",
                          }))
                        }
                      />
                      <label className="form-check-label" htmlFor="statusCheckbox">
                        {formData.status === "Active" ? "Active" : "Inactive"}
                      </label>
                    </div>
                    {errors.status && <small className="text-danger d-block mt-1">{errors.status}</small>}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "system" && (
              <div>
                <div className="form-check mb-2 text-start">
                  <input type="checkbox" className="form-check-input" id="inactiveCheckbox" checked={!isActive} disabled />
                  <label className="form-check-label ms-2" htmlFor="inactiveCheckbox">
                    INACTIVE
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
              <button type="submit" form="employeeForm" className="btn btn-primary me-2">
                Save
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
